import { weenatIntl as wintl } from '@weenat/wintl';
import isEmpty from 'lodash-es/isEmpty';
import isFunction from 'lodash-es/isFunction';
import isNil from 'lodash-es/isNil';
import omit from 'lodash-es/omit';
import moment from 'moment-timezone';
import { useMemo, useState } from 'react';
import { getConvertedAlertFieldSettings, getPredicateMetric, getSchemaNamePerOperation, schemasInitialValues } from '.';
import { useOrderedFields } from '..';
import { useConvertedValue } from '../../hooks';
import { getPredicateKey } from '../../resources/alerts.types';
import schemas from '../../schemas';
import { useAllHorizonsOptions } from '../horizons';
const ALERT_PREDICATE_PARAM_SETUP_SCHEMA = schemas.alertCreation.predicateParamsSetups;
// For capacitive only 10, 20, 30, 40, 50, 60 cm depths are useful
const ALLOWED_CAPACITIVE_HORIZON_IDS = [2, 4, 6, 8, 10, 12];
const ALLOWED_HORIZON_IDS_BY_SOURCE = {
    IRRI_CAPA: ALLOWED_CAPACITIVE_HORIZON_IDS,
    T_CAPA: ALLOWED_CAPACITIVE_HORIZON_IDS
};
const useSetupPredicateParams = (predicate, formOrSubmit) => {
    const availableHorizonOptions = useAllHorizonsOptions(false, ALLOWED_HORIZON_IDS_BY_SOURCE[predicate.source]);
    const { op, source, params } = predicate;
    const { getUnit, convertNumberValue } = useConvertedValue();
    const predicateKey = getPredicateKey(predicate);
    const metric = getPredicateMetric(predicate);
    const unit = getUnit(metric);
    const [finalParams, setFinalParams] = useState(params);
    const schemaName = useMemo(() => getSchemaNamePerOperation(op, source), [op, source]);
    const since = 'since' in params && !isNil(params.since)
        ? moment.isDate(params.since)
            ? params.since
            : moment.unix(params.since).toDate()
        : 'since' in params
            ? moment(new Date(Date.now())).startOf('day').toDate()
            : undefined;
    const initialValues = !isEmpty(params)
        ? {
            // This field is not a user defined one but derived from input
            ...omit(params, 'initialAcc'),
            ...(!isNil(since) ? { since } : {})
        }
        : schemaName
            ? schemasInitialValues(metric, unit)[schemaName]
            : {};
    const schema = useMemo(() => {
        let tempSchema;
        const schemaOrMethod = ALERT_PREDICATE_PARAM_SETUP_SCHEMA[schemaName];
        if (!isFunction(schemaOrMethod)) {
            tempSchema = schemaOrMethod;
        }
        else {
            tempSchema = schemaOrMethod({ metric, convertNumberValue });
        }
        return tempSchema;
    }, [schemaName, schemas, metric]);
    const fieldSettings = useMemo(() => {
        let settings = {};
        if (!isNil(schemaName)) {
            const allSchemas = getConvertedAlertFieldSettings({ metric, convertNumberValue });
            settings = allSchemas[schemaName] || {};
        }
        return settings;
    }, [schemaName, metric, convertNumberValue, getConvertedAlertFieldSettings]);
    const appendUnit = ({ msg, withParentheses }) => {
        const unitAbbr = wintl.t(`units.${unit}_abbr`);
        return isEmpty(unitAbbr) ? msg : withParentheses ? `${msg} (${unitAbbr})` : `${msg} ${unitAbbr}`;
    };
    const schemaKeys = Object.keys(schema.initialValues);
    const orderedFields = useOrderedFields(schemaKeys, formOrSubmit);
    return {
        appendUnit,
        convertNumberValue,
        fieldSettings,
        finalParams,
        initialValues,
        metric,
        op,
        orderedFields,
        predicateKey,
        schema,
        setFinalParams,
        source,
        availableHorizonOptions
    };
};
export default useSetupPredicateParams;
